import React, { useEffect, useMemo, useRef, useState } from "react";
import "./ReportTemplate.scss";

import classNames from "classnames";
import RGL, { WidthProvider } from "react-grid-layout";
import { useDispatch, useSelector } from "react-redux";

import { injectIntl } from "react-intl";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { MetaFieldType } from "src/app/methods/getMetaFieldTypeOptions";
import { translateMessage } from "src/app/methods/translateMessage";
import { addTableSummaryToLayout } from "src/app/generator-data/functions/addTableSummaryToLayout";
import { addTableOfContentsToLayout } from "src/app/generator-data/functions/addTableOfContentsToLayout";
import { addCampaignDetailsToLayout } from "src/app/generator-data/functions/addCampaignDetailsToLayout";
import { addCreatorsToLayout } from "src/app/generator-data/functions/addCreatorsToLayout";
import { addPublicationsToLayout } from "src/app/generator-data/functions/addPublicationsToLayout";
import { IDH_BRAND_COLOR } from "src/constants/idhBrandColor";
import { addCommentsAnalysisToLayout } from "src/app/generator-data/functions/comments-analysis/addCommentsAnalysisToLayout";
import { addAudienceOverlapToLayout } from "src/app/generator-data/functions/audience-overlap/addAudienceOverlapToLayout";
import {
  setNumberedHeaders,
  setReportGridElements,
  setReportHiddenGridElements,
  setReportLayout,
  setReportSavedTemplate,
  setReportSeparators,
} from "../../../../redux";
import CampaignPlaceholder from "../../../../images/project-placeholder.svg";
import { getImageSource } from "../../../../utils/methods";
import { ShowcaseLoader } from "../../../CreatorShowcase/components/ShowcaseLoader/ShowcaseLoader";
import { ShowcasePrintingLoader } from "../../../CreatorShowcase/components/ShowcasePrintingLoader/ShowcasePrintingLoader";
import { ContentBox } from "../../../generator-data/components/ContentBox/ContentBox";
import { addGlossaryToLayout } from "../../../generator-data/functions/addGlossaryToLayout";
import { addPostsDataToLayout } from "../../../generator-data/functions/addPostsDataToLayout";
import { addSeparatorsToLayout } from "../../../generator-data/functions/addSeparatorsToLayout";
import { addSummaryLearningsToLayout } from "../../../generator-data/functions/addSummaryLearningsToLayouts";
import { renderMetaField } from "../../../generator-data/functions/renderMetaField";
import { addCreatorInsightsToLayout } from "../../functions/addCreatorInsightsToLayout";
import { addHeaderToLayout } from "../../functions/addHeaderToLayout";
import { getNumberedHeaders } from "../../functions/getNumberedHeaders";
import { getReportGridElements } from "../../functions/grid-elements/getReportGridElements";
import { renderAudienceData } from "../../functions/rendering-functions/renderAudienceData";
import { renderCreator } from "../../functions/rendering-functions/renderCreator";
import { renderCreatorInsights } from "../../functions/rendering-functions/renderCreatorInsights";
import { renderHeader } from "../../functions/rendering-functions/renderHeader";
import { renderPostsData } from "../../functions/rendering-functions/renderPostsData";
import { renderPublication } from "../../functions/rendering-functions/renderPublication";
import { GeneratorLoader } from "../GeneratorLoader/GeneratorLoader";
import { ReportFooter } from "../ReportFooter/ReportFooter";
import { ReportSummaryLearnings } from "../ReportSummaryLearnings/ReportSummaryLearnings";
import { CoverBox } from "./components/CoverBox/CoverBox";
import { ReportGridBox } from "./components/ReportGridBox/ReportGridBox";
import TableOfContents from "./components/TableOfContents/TableOfContents";
import { TitleBox } from "./components/TitleBox/TitleBox";
import { ReportGlossaryPartOne } from "../ReportGlossary/ReportGlossaryPartOne";
import { ReportGlossaryPartTwo } from "../ReportGlossary/ReportGlossaryPartTwo";
import { renderComments } from "../../functions/rendering-functions/renderComments";
import { renderSentimentAnalysis } from "../../functions/rendering-functions/renderSentimentAnalysis";
import { renderTableSummary } from "../../functions/rendering-functions/renderTableSummary";
import { addAudienceDataToLayout } from "../../functions/addAudienceDataToLayout";
import { getAggregatedOrFirstByKey } from "../ReportToolbar/components/DataAggregation/utils";
import { renderOverlapPlatform } from "../../functions/rendering-functions/renderOverlapPlatform";
import { renderOverlapChart } from "../../functions/rendering-functions/renderOverlapChart";
import { renderOverlapCreator } from "../../functions/rendering-functions/renderOverlapCreator";

const ReactGridLayout = WidthProvider(RGL);

export const META_TYPES_WHITELIST = [
  MetaFieldType.Text,
  MetaFieldType.Number,
  MetaFieldType.Currency,
  MetaFieldType.Percent,
  MetaFieldType.SingleSelect,
  MetaFieldType.MultiSelect,
  MetaFieldType.AggregatedSelect,
  MetaFieldType.Rating,
];

export const FIELDS_BLACKLIST = [
  "project_name",
  "title",
  "owner",
  "completed",
  "socialProfiles",
];

export const TIK_TOK_BLACKLIST = ["credibility", "location-by-city"];
export const YOUTUBE_BLACKLIST = [
  "credibility",
  "location-by-city",
  "keywords",
  "creators-brand-affinity",
  "audience-interests",
  "audience-brand-affinity",
  "creator-interests",
];

export const STATIC_ELEMENTS = ["cover-box", "title-box", "separator"];

function ReportTemplate(props) {
  const {
    format,
    pages,
    setPages,
    templateApplied,
    setTemplateApplied,
    coverImage,
    intl,
  } = props;

  const templateRef = useRef();

  const initialLayout = [
    { i: "cover-box", x: 0, y: 0, w: 12, h: 13, static: true },
    { i: "title-box", x: 0, y: 13, w: 12, h: 3, static: true },
  ];

  const [shouldRerenderLayout, setShouldRerenderLayout] = useState(true);

  const dispatch = useDispatch();
  const {
    projectReducer: { membersList },
    reportReducer: {
      gridElements,
      hiddenGridElements,
      layout,
      loading,
      printing,
      showLoader,
      loaderText,
      savedTemplate,
      selectedTemplate,
      separators,
      newlyAddedPages,
      campaigns,
      creatorsInsights,
      sentimentData,
      creators,
      dataAggregation,
      canDisplaySentiments,
    },
    taskReducer: { contentAutocomplete },
    mainReducer: { identity },
  } = useSelector((state) => state);

  const data = useMemo(() => {
    return getAggregatedOrFirstByKey(campaigns, "project");
  }, [campaigns]);

  const { comments } = sentimentData || {};

  const brandColor =
    identity?.wsWorkspace?.customPrimaryColor ?? IDH_BRAND_COLOR;

  const setLayout = (newLayout) => {
    dispatch(setReportLayout(newLayout));
  };

  const setGridElements = (newGridElements) => {
    dispatch(setReportGridElements(newGridElements));
  };

  const setHiddenGridElements = (newHiddenGridElements) => {
    dispatch(setReportHiddenGridElements(newHiddenGridElements));
  };

  const setSeparators = (newSeparators) => {
    dispatch(setReportSeparators(newSeparators));
  };

  useEffect(() => {
    if (savedTemplate.length) {
      setTemplateApplied(false);
    }
  }, [savedTemplate]);

  useEffect(() => {
    if (!templateApplied) {
      getGridElements();
      // showToast("success", "Success", "Saved template has been applied!");
    }
  }, [templateApplied]);

  useEffect(() => {
    getGridElements();
  }, [
    data.metadata,
    format,
    creatorsInsights,
    comments,
    campaigns,
    canDisplaySentiments,
    // numberedHeaders,
  ]);

  useEffect(() => {
    if (selectedTemplate.value.type === "default") {
      getGridElements();
    }
  }, [selectedTemplate]);

  // Rendering

  useEffect(() => {
    if (!shouldRerenderLayout) return;

    // applying saved template
    if (savedTemplate?.length && !templateApplied) {
      applySavedLayout();
    }
    // applying default template
    // else if (selectedTemplate?.value?.type === "default") {
    //   applyChosenTemplate();
    // }
    else {
      applyDefaultLayout();
    }
    setShouldRerenderLayout(false);
  }, [gridElements, format.pageWidth]);

  const applySavedLayout = () => {
    setLayout(savedTemplate);

    dispatch(setReportSavedTemplate([]));
    setTemplateApplied(true);
  };

  const applyDefaultLayout = () => {
    const newLayout = [...initialLayout];
    const newSeparators = [];

    // Table of contents
    addTableOfContentsToLayout(newLayout, gridElements);

    // Campaign details
    addHeaderToLayout("campaign-overview", newLayout, format, 1);
    addHeaderToLayout("metadata", newLayout, format, 2);
    addCampaignDetailsToLayout(
      campaigns,
      gridElements,
      newLayout,
      newSeparators,
      format,
    );

    // Creators insights
    addHeaderToLayout("creator-insights", newLayout, format, 2);
    addCreatorInsightsToLayout(
      gridElements,
      newLayout,
      format,
      campaigns,
      dataAggregation,
    );

    // Creators in the campaign
    addHeaderToLayout("creator", newLayout, format, 2);
    addCreatorsToLayout(
      gridElements,
      newLayout,
      format,
      campaigns,
      dataAggregation,
    );

    // Campaign audience data
    addHeaderToLayout("audience-data", newLayout, format, 2);
    addAudienceDataToLayout(
      gridElements,
      newLayout,
      format,
      campaigns,
      dataAggregation,
    );

    // Audience overlap
    addHeaderToLayout("audience-overlap", newLayout, format, 2);
    addAudienceOverlapToLayout(newLayout, format, campaigns, dataAggregation);

    // Campaign data
    addHeaderToLayout("campaign-data", newLayout, format, 1);
    addHeaderToLayout("post-data", newLayout, format, 2);
    addPostsDataToLayout(
      gridElements,
      newLayout,
      format,
      campaigns,
      dataAggregation,
    );

    // All Publications
    addHeaderToLayout("publication", newLayout, format, 1);
    // addTopPerformingPublicationsToLayout(
    //   gridElements,
    //   newLayout,
    //   format,
    //   campaigns,
    //   dataAggregation,
    // );
    addHeaderToLayout("publication", newLayout, format, 2);
    addPublicationsToLayout(
      gridElements,
      newLayout,
      format,
      campaigns,
      dataAggregation,
    );

    // Table summary
    addHeaderToLayout("table-summary", newLayout, format, 1);
    addTableSummaryToLayout(newLayout, format, campaigns, dataAggregation);

    // Comments analysis
    if (canDisplaySentiments) {
      addHeaderToLayout("comments-analysis", newLayout, format, 1);
      addCommentsAnalysisToLayout(
        newLayout,
        format,
        campaigns,
        dataAggregation,
      );
    }

    // Summary & Learnings
    addHeaderToLayout("summary-learnings", newLayout, format, 1);
    addSummaryLearningsToLayout(newLayout);

    // Glossary
    addHeaderToLayout("glossary", newLayout, format, 1);
    addGlossaryToLayout(newLayout);
    setLayout(newLayout);
    setSeparators(newSeparators);
  };

  useEffect(() => {
    if (!layout?.length) return;

    dispatch(setNumberedHeaders(getNumberedHeaders(layout, gridElements)));

    const functionProps = {
      dispatch,
      format,
      gridElements,
      layout,
      newlyAddedPages,
      pages,
      separators,
      //
      setPages,
      setLayout,
      setSeparators,
    };

    addSeparatorsToLayout(functionProps);
  }, [layout]);

  const formatMessage = ({ id, defaultMessage }) => {
    return translateMessage({ intl, id, defaultMessage });
  };

  const getGridElements = async () => {
    if (!data?.title) return;

    const functionProps = {
      contentAutocomplete,
      dataAggregation,
      formatMessage,
      membersList,
      campaigns,
      savedTemplate,
      templateApplied,
      // numberedHeaders,
      dispatch,
      setGridElements,
      setHiddenGridElements,
      setShouldRerenderLayout,
      canDisplaySentiments,
      previousGridElements: gridElements,
      previousHiddenGridElements: hiddenGridElements,
    };

    await getReportGridElements(functionProps);
  };

  const ShowcaseBoxes = useMemo(() => {
    return gridElements.map((box) => {
      const campaign = campaigns[box.campaignId] || {};

      switch (box.gridBoxType) {
        case "metadata": {
          const { project } = campaign;
          return renderMetaField(box, project, ReportGridBox);
        }
        case "header":
          return renderHeader(box, ReportGridBox, box.depth > 1);
        case "creator": {
          const { creators } = campaign;
          return renderCreator(box, ReportGridBox, creators);
        }
        case "publication":
        case "top-publication": {
          const { publications, creators } = campaign;
          return renderPublication(box, ReportGridBox, publications, creators);
        }
        case "post-data": {
          const { project } = campaign;
          return renderPostsData(box, ReportGridBox, project.metadata);
        }
        case "audience-data": {
          const { audienceData } = campaign;
          return renderAudienceData(box, audienceData, layout);
        }
        case "creator-insights": {
          const { creatorsInsights } = campaign;
          return renderCreatorInsights(box, creatorsInsights, layout);
        }
        case "overlap-platform": {
          return renderOverlapPlatform(box, ReportGridBox);
        }
        case "overlap-chart": {
          return renderOverlapChart(box, ReportGridBox);
        }
        case "overlap-creator":
          return renderOverlapCreator(box, ReportGridBox);
        case "paragraph":
          return (
            <ReportGridBox key={box.label} id={box.label}>
              <ContentBox
                key={box.label}
                fieldName="Paragraph"
                data={data}
                block
                textField
              />
            </ReportGridBox>
          );
        case "text":
          return (
            <ReportGridBox key={box.label} id={box.label}>
              <ContentBox
                key={box.label}
                fieldName="Text field"
                data={data}
                block
                textField
              />
            </ReportGridBox>
          );
        case "sentiment-analysis":
          return renderSentimentAnalysis(box, ReportGridBox);
        case "table-summary":
          return renderTableSummary(box, ReportGridBox, layout);
        case "comments":
          return renderComments(box, ReportGridBox);
        case "summary-learnings":
          return (
            <ReportGridBox id={box.label} key={box.label}>
              <ReportSummaryLearnings />
            </ReportGridBox>
          );
        case "glossary":
          return (
            <ReportGridBox id={box.label} key={box.label} smallPadding>
              <ReportGlossaryPartOne />
            </ReportGridBox>
          );
        case "glossary-two":
          return (
            <ReportGridBox id={box.label} key={box.label} smallPadding>
              <ReportGlossaryPartTwo />
            </ReportGridBox>
          );
        case "table-of-contents":
          return (
            <ReportGridBox
              id={box.label}
              key={box.label}
              className="table-of-contents-wrapper"
              disableEditing
            >
              <TableOfContents part={box.part} pages={pages} />
            </ReportGridBox>
          );
        default:
          return null;
      }
    });
  }, [gridElements, layout, format, data, comments, campaigns]);

  const onLayoutChange = (newLayout) => {
    setLayout(newLayout);
  };

  const filteredLayout = layout.filter(
    (item) =>
      gridElements?.find((box) => item.i === box.label) ||
      STATIC_ELEMENTS.some((el) => item.i.includes(el)),
  );

  return (
    <div
      ref={templateRef}
      className={classNames("report-template", {
        "report-template--loading": loading || printing,
      })}
      style={{
        paddingTop: `${format.rowHeight / 2}px`,
        width: `${format.pageWidth}px`,
        "--before-color": brandColor,
      }}
    >
      {showLoader && <GeneratorLoader text={loaderText} />}

      {printing && <ShowcasePrintingLoader />}

      {loading ? (
        <ShowcaseLoader />
      ) : (
        <>
          <ReactGridLayout
            style={{ width: `${format.pageWidth}px` }}
            className="layout"
            layout={filteredLayout}
            cols={format.cols}
            rowHeight={format.rowHeight}
            compactType={null}
            margin={[24, 20]}
            preventCollision
            onLayoutChange={onLayoutChange}
            draggableHandle=".react-drag-handle"
          >
            <ReportGridBox
              id="cover-box"
              key="cover-box"
              className="cover-box-wrapper"
              disableEditing
            >
              <CoverBox data={coverImage} creators={creators} format={format} />
            </ReportGridBox>

            <ReportGridBox id="title-box" key="title-box" smallPadding>
              <TitleBox />
            </ReportGridBox>

            {ShowcaseBoxes}

            {separators.map((item, index) => (
              <div
                id={item.key}
                key={item.key}
                className="report-template__separator"
              >
                <div
                  className={classNames("report-template__separator-line", {
                    "report-template__separator-line--hidden": showLoader,
                  })}
                />
                <div className="report-template__separator-provider">
                  {Object.keys(campaigns).length === 1 ? (
                    <img
                      className="report-template__separator-campaign-logo"
                      src={getImageSource(
                        data?.cover,
                        "tiny",
                        CampaignPlaceholder,
                      )}
                      alt="campaign"
                    />
                  ) : null}
                  {data.title}
                </div>
                {index + 1 >= 2 && (
                  <div className="report-template__separator-page">
                    <IDHFormattedMessage id="ws_page" defaultMessage="Page" />
                    &nbsp;|&nbsp;
                    <span className="report-template__separator-page-number">
                      {index + 1}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </ReactGridLayout>

          <ReportFooter data={data} />
        </>
      )}
    </div>
  );
}

export default injectIntl(ReportTemplate);
